/**
 * rutasApp.js — los primeros segmentos de ruta que pertenecen al SPA.
 *
 * ── PARA QUÉ ────────────────────────────────────────────────────────────────
 *
 * Para que nginx pueda devolver un **404 de verdad**.
 *
 * Hasta aquí el fallback era incondicional: `try_files $uri $uri/index.html
 * /app.html`, así que CUALQUIER url inventada devolvía 200 con la cáscara del
 * SPA. Google lo detecta y lo llama «soft 404»: no lo indexa pero sí lo
 * rastrea, una y otra vez, gastando presupuesto en urls que no existen.
 *
 * El problema es que nginx no puede distinguir «/settings/general, que es una
 * ruta del SPA y hay que servirle la cáscara» de «/pagina-inventada, que no
 * existe». Solo el router lo sabe. Esta lista es esa información, en un formato
 * que nginx pueda leer.
 *
 * ── POR QUÉ ESTÁ ESCRITA A MANO Y AUN ASÍ NO SE PUEDE OLVIDAR ───────────────
 *
 * Derivarla en tiempo de build importando `pages.config.js` sería tentador,
 * pero ese módulo hace `import __Layout from './Layout.jsx'` —estático— y eso
 * arrastraría `AuthContext`, `gbusinessService` y medio panel al bundle de SSR
 * del prerender, que hoy no los toca.
 *
 * Así que la lista se escribe, y lo que impide que se quede vieja es un TEST:
 * `rutasApp.test.js` lee los `<Route path>` de `App.jsx` y las claves de
 * `PAGES` en `pages.config.js`, y falla si alguna no está cubierta aquí.
 * Añadir una ruta al SPA y olvidarse de esta lista rompe la suite antes de
 * llegar a producción.
 *
 * Eso es lo único que separa esto del incidente del §52: un `location` sin el
 * delimitador correcto se tragó `/en1/...` y tiró abajo todos los LinkTree
 * públicos. La diferencia entre un soft-404 y un cliente viendo un 404 en una
 * página que existe es enorme, y el segundo no se detecta hasta que alguien lo
 * reporta.
 *
 * ── SON PRIMEROS SEGMENTOS, NO RUTAS ────────────────────────────────────────
 *
 * `settings` cubre `/settings/general`, `/settings/integrations` y las que
 * vengan. `en1` cubre `/en1/<id>`. Lo que se compara es el primer segmento
 * entero, anclado en `/` o en el final de la cadena — NUNCA con un
 * `startsWith` a pelo, que es lo que confundiría `/en1/abc` con el prefijo de
 * idioma `/en`.
 */

/**
 * Rutas declaradas a mano en `App.jsx`, fuera del auto-montaje.
 *
 * `redirect` y `en1` son las dos que más duelen si se caen: la primera la
 * reciben los QR impresos y la segunda los LinkTree públicos, y ninguna de las
 * dos se puede cambiar porque están en papel.
 */
const EXPLICITAS = [
  'login',
  'register',
  'password-reset',
  'password-reset-confirm',
  'redirect',
  'en1',
  'join',
  'link-pago',
  // La landing del Plano Google Maps pt: la del correo (enlace mágico,
  // `/oferta/<token>`) y la de quien entra con sesión.
  'oferta',
  'plano-google-maps',
  'auditor',
  'capturas',
  'v3',
  'location',
  'settings',
  'ChangePassword',
  'Integraciones',
  // SEO Score v2: solo existe donde su flag la monta (staging/local), pero
  // donde exista tiene que poder recargarse sin que nginx conteste 404.
  'GMBHealthScoreV2',
];

/**
 * Las páginas del panel, que `App.jsx` monta en bucle desde `PAGES` de
 * `pages.config.js` con `path={`/${clave}`}`.
 *
 * Copiadas y no importadas, por lo que dice la cabecera. El test comprueba que
 * esta lista y la de allí coincidan.
 */
const DEL_PANEL = [
  'AdminAlerts', 'AdminBulkProducts', 'AdminCampanaGoogleBusiness', 'AdminCampanaPt',
  'AdminCtaLinks', 'AdminDashboard',
  'AdminDemoAccounts', 'AdminGmb', 'AdminIncludedDevices', 'AdminInformeMapa', 'AdminLeads',
  'AdminMap', 'AdminMeetings', 'AdminMetrics', 'AdminProductDetail',
  'AdminProducts', 'AdminProspeccion', 'AdminProtectionReview', 'AdminScoreboard', 'AdminSettings',
  'AdminUserDetail', 'AdminUsers',
  'Analytics', 'Automations', 'Devices', 'EmployeeRanking', 'GMBGeoGrid',
  'GMBHealthScore', 'GMBMetrics', 'GMBProfile', 'GMBPublications',
  'GbusinessConnect', 'Home', 'LinkTree', 'LinkTreeConfig', 'LocalesCompare',
  'LocationDetail', 'MyBusiness', 'ProductDetail', 'ProductUpdate', 'Profile',
  'RegisterQR', 'ReportsInformes', 'ReportsKeywords', 'ReportsNPS',
  'ReportsSentiment', 'Reviews', 'ScanQR', 'SelectLocation',
  'SettingsNotifications', 'SettingsOrganization', 'SuggestFeatures',
];

/** Todos, sin repetidos y ordenados para que el fragmento de nginx sea estable. */
export const PREFIJOS_APP = [...new Set([...EXPLICITAS, ...DEL_PANEL])].sort();

const PREFIJOS_EN_MINUSCULA = new Set(PREFIJOS_APP.map((p) => p.toLowerCase()));

/**
 * ¿Es una ruta que el SPA sirve, aunque sea para pedir sesión?
 *
 * ── PARA QUÉ ────────────────────────────────────────────────────────────────
 *
 * Para que un visitante sin sesión que pide una URL que NO EXISTE vea la página
 * de error y no el formulario de login.
 *
 * `App.jsx` manda al login todo lo que no esté en `PUBLIC_PATHS`, y eso incluye
 * lo que no existe. O sea que la ruta comodín `<Route path="*">` nunca llegaba a
 * pintarse para un anónimo: pedir `/pagina-inventada` te daba un «Inicia sesión
 * para continuar», que es a la vez confuso —te pide credenciales para algo que
 * no hay— y una pista de que quizá esa página existe si te autenticas.
 *
 * Con esto, la respuesta a una URL inventada es la misma para todos: no existe.
 *
 * Es el mismo conocimiento que ya usa nginx, leído de la misma lista, así que
 * los dos no pueden discrepar: lo que nginx considera del SPA es lo que aquí
 * lleva al login, y lo que nginx devuelve como 404 es lo que aquí pinta la
 * página de error.
 *
 * Compara en minúsculas porque react-router casa sin distinguir mayúsculas:
 * `/profile` y `/Profile` son la misma ruta y las dos tienen que reconocerse.
 */
export function esRutaDeApp(pathname) {
  if (typeof pathname !== 'string') return false;
  const primero = pathname.replace(/^\/+/, '').split('/')[0].toLowerCase();
  if (!primero) return true; // la raíz, que es la home
  return PREFIJOS_EN_MINUSCULA.has(primero);
}

/**
 * El fragmento de configuración que lee nginx.
 *
 * ── LOS TRES DETALLES QUE IMPORTAN ──────────────────────────────────────────
 *
 * 1. **`(/|$)` al final del grupo.** Es el delimitador. Sin él, `^/(…|en1|…)`
 *    casaría también con `/en1cualquiercosa`, y peor: un prefijo corto se
 *    tragaría urls que no son suyas. Es literalmente el fallo del §52.
 * 2. **`~*`, insensible a mayúsculas.** El router casa así (`/profile` y
 *    `/Profile` son la misma ruta), de modo que nginx tiene que hacer lo mismo
 *    o `/profile` daría 404 mientras `/Profile` funciona.
 * 3. **Va DESPUÉS de las redirecciones 301 del fichero principal.** nginx
 *    evalúa los `location` con regex en orden de aparición y se queda con el
 *    primero que case. Si esto fuera antes, `/Terms` casaría aquí —`terms` no
 *    está en la lista, pero `/Cookies` sí sería el caso si lo estuviera— y la
 *    redirección no llegaría a ejecutarse.
 *
 * El `try_files /app.html =404` del cuerpo es lo que sirve la cáscara. El
 * `=404` es defensivo: si `app.html` no se hubiera generado, mejor un error
 * claro que servir otra cosa.
 */
export function fragmentoNginx() {
  return [
    '# GENERADO POR scripts/prerender.mjs — NO EDITAR A MANO.',
    '#',
    '# Las rutas que pertenecen al SPA. Todo lo que no case con esto ni sea un',
    '# fichero prerenderizado devuelve un 404 de verdad, no la cáscara con 200.',
    '#',
    `# Sale de src/lib/rutasApp.js (${PREFIJOS_APP.length} prefijos). Si añades una`,
    '# ruta al router y no a esa lista, rutasApp.test.js lo caza.',
    `location ~* ^/(${PREFIJOS_APP.join('|')})(/|$) {`,
    '    add_header Cache-Control "no-cache, no-store, must-revalidate";',
    '    try_files /app.html =404;',
    '}',
    '',
  ].join('\n');
}
